import { Autocomplete, TextField, Chip } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Case, CaseStatus, Maybe, OrderCaseBy, useFetchAllMattersWithoutJoinLazyQuery } from '../../../../generated'
import { PAGE_LIMIT } from '../../../constants'

interface MatterMultiSelectFilterProps {
  matterIds: string[] | undefined
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const MatterMultiSelectFilter: FC<MatterMultiSelectFilterProps> = ({ matterIds, setReportFilters }) => {
  const [cases, setCases] = useState<Maybe<Case>[]>([])
  const [selectedCases, setSelectedCases] = useState<Case[]>([])
  const [casePage, setCasePage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch] = useDebounce(searchTerm, 500)

  const [fetchCases, { loading }] = useFetchAllMattersWithoutJoinLazyQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.fetchAllMattersWithoutJoin
      if (!result) return
      const { cases: newCases, pagination } = result
      const { page: currentPage, totalPages } = pagination || {}
      if (newCases) {
        setCases(prev => (casePage === 1 ? newCases : [...prev, ...newCases]))
        setHasMorePages(currentPage && totalPages ? currentPage < totalPages : false)
      }
    },
    onError() {
      setCases([])
      setHasMorePages(false)
    }
  })

  useEffect(() => {
    setCases([])
    setCasePage(1)
    setHasMorePages(true)
  }, [debouncedSearch])

  useEffect(() => {
    if (!hasMorePages) return
    fetchCases({
      variables: {
        casesInput: {
          orderBy: OrderCaseBy.Recent,
          status: CaseStatus.Active,
          searchValue: debouncedSearch.trim(),
          isConsultCase: undefined,
          paginationOptions: { limit: PAGE_LIMIT, page: casePage }
        }
      }
    })
  }, [casePage, debouncedSearch, fetchCases, hasMorePages])

  const getOptionLabel = (option: Case) => {
    const num = option?.isConsultMatter ? option.consultMatterNumber : option?.caseNumber
    return num ? `(#${num}) ${option.title || ''}` : option.title || ''
  }

  // Merge search results with selected cases so selected options are always available
  const options = [
    ...selectedCases,
    ...(cases.filter(Boolean) as Case[]).filter(c => !selectedCases.some(s => s.id === c.id))
  ]

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const node = event.currentTarget
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 5 && hasMorePages && !loading) {
        setCasePage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  return (
    <Autocomplete
      multiple
      options={options}
      value={selectedCases}
      getOptionLabel={option => getOptionLabel(option as Case)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_, newValue) => {
        setSelectedCases(newValue)
        setReportFilters(prev => ({
          ...prev,
          matterIds: newValue.map(c => c.id)
        }))
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') setSearchTerm(newInput)
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={getOptionLabel(option)}
            size="small"
          />
        ))
      }
      renderInput={params => <TextField {...params} label="Matters" />}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' },
        onScroll: handleScroll
      }}
      loading={loading}
      noOptionsText={loading ? 'Loading...' : 'No matters'}
      disableCloseOnSelect
      sx={{ minWidth: 300, maxWidth: 400 }}
    />
  )
}

export default MatterMultiSelectFilter
